import { NgClass } from '@angular/common';
import { Component, OnDestroy, Type } from '@angular/core';
import { ListAccountingPlanSetupsComponent } from 'components/accounting/accounting-plan/list-accounting-plan-setups.component';
import { InvoiceOverdueSearchComponent } from 'components/accounting/debt-collection/invoice-overdue-search.component';
import { InvoiceControlListComponent } from 'components/accounting/invoice-control-list.component';
import { InvoiceObjectionSearchComponent } from 'components/accounting/invoice-objection-search/invoice-objection-search.component';
import { SearchLedgerPostsComponent } from 'components/accounting/ledger/search-ledger-posts.component';
import { OverriddenDueDatesComponent } from 'components/accounting/overridden-due-dates.component';
import { ListPriceAdjustmentTablesComponent } from 'components/accounting/price-adjustments/list-price-adjustment-tables.component';
import { SearchProductPlansComponent } from 'components/accounting/product-plan/search-product-plans.component';
import { CleanUpDomainsComponent } from 'components/admin/clean-up/clean-up-domains.component';
import { ListMailTemplatesComponent } from 'components/admin/mail-template/list-mail-templates.component';
import { ListPoolComponent } from 'components/admin/pool/list-pool.component';
import { ProductPlanSettingsComponent } from 'components/admin/product-plan-settings/product-plan-settings.component';
import { ListVariantListsComponent } from 'components/admin/product-variantlist/list-variant-lists.component';
import { ListProductsComponent } from 'components/admin/product/list-products.component';
import { ListReasonsComponent } from 'components/admin/reason/list-reasons.component';
import { ListLogComponent } from 'components/admin/system-log/view-log.component';
import { ListTeamComponent } from 'components/admin/team/list-team.component';
import { ListUserComponent } from 'components/admin/user/list-user.component';
import { DropdownMenu } from 'components/layout/dropdown-menu/dropdown-menu.component';
import { IconComponent, IconType } from 'components/layout/icon/icon.component';
import { ListQueryComponent } from 'components/query/list-query.component';
import { ListQuestionnairesComponent } from 'components/questionnaire/list-questionnaires.component';
import { LeadsListComponent } from 'components/sale/leads/list/leads-list.component';
import { ListOrdersWithWarningsComponent } from 'components/sale/list-orders-with-warnings.component';
import { OpenCallsComponent } from 'components/sale/open-calls/opencalls.component';
import { OrderOnboardingListComponent } from 'components/sale/order-onboarding/order-onboarding-list.component';
import { OrderPipelineComponent } from 'components/production/order-pipeline.component';
import { MyOrdersComponent } from 'components/sale/orders/my-orders.component';
import { ListUnapprovedSalesComponent } from 'components/sale/unapproved-sales/list-unapproved-sales.component';
import { UnassignedQualificationCallsComponent } from 'components/sale/unassigned-qualification-calls/unassigned-qualification-calls.component';
import { SettlementsListComponent } from 'components/settlement/settlements-list.component';
import { ListTaskAreasComponent } from 'components/task/task-areas/list-task-areas.component';
import { ListTaskflowTemplateComponent } from 'components/task/taskflow-template/list-taskflow-template.component';
import { ListTaskActionsComponent } from 'components/task/taskflow/list-task-actions.component';
import { DebugContextMenuComponent } from 'components/test/debug-context-menu/debug-context-menu.component';
import { DebugInputFieldsComponent } from 'components/test/debug-input-fields/debug-input-fields.component';
import { DebugPanelLayoutComponent } from 'components/test/debug-layout/debug-panel-layout.component';
import { DebugListViewComponent } from 'components/test/debug-list-view/debug-list-view.component';
import { DebugOrderComponent } from 'components/test/debug-order/debug-order.component';
import { SyncDataComponent } from 'components/test/sync/sync-data.component';
import { TestSearchCompanyComponent } from 'components/test/test-searchcompany/test-searchcompany.component';
import { environment } from 'environments/environment';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CredentialService } from 'services/credential.service';
import { NavigationService } from 'services/navigation.service';
import { PoolService } from 'services/pool.service';
import { PushService } from 'services/push.service';
import { UserService } from 'services/user.service';
import { DashboardComponent } from 'components/core/dashboard.component';
import { UserModel } from 'models/generated/Identity.Api.Models.User';
import { UserPipe } from 'pipes/user.pipe';
import { UserImageDirective } from 'directives/user-image.directive';
import { ListDashboardComponent } from 'components/admin/dashboard/list-dashboard.component';
import { UserSettingsComponent } from 'components/admin/user/user-settings.component';

@Component({
	selector: 'nav-menu',
	templateUrl: './navmenu.component.html',
	styleUrls: ['./navmenu.component.scss'],
	imports: [DropdownMenu, IconComponent, UserImageDirective, NgClass, TranslatePipe, UserPipe]
})
export class NavMenuComponent implements OnDestroy {

	user: UserModel;
	menu = new Menu();
	activeSubMenu?: SubMenu;
	activeMenuItem?: MenuItem;
	logoLink: MenuItem = new MenuItem('dashboard', DashboardComponent);
	quickLinks: QuickLinkItem[] = [];
	openMenu?: SubMenu;

	private poolChangeSubscription: Subscription;
	private credentialSubscription: Subscription;
	private menuTemplate: Menu;

	constructor(private readonly navigation: NavigationService
		, private readonly userService: UserService
		, private readonly credentialService: CredentialService
		, private readonly pushService: PushService
		, private readonly poolService: PoolService) {

		this.user = this.userService.getCurrentUser();
		this.setQuickLinks();

		this.poolChangeSubscription = this.pushService.onPoolChanges.subscribe(() => {
			this.setQuickLinks();
		});
		this.credentialSubscription = this.userService.onCredentialsChanged().subscribe(() => {
			this.setAvailableMenuItems();
		});

		this.menuTemplate = new Menu();
		this.menuTemplate.subMenus.push(
			new SubMenu('admin', [
				new MenuItem('product-admin', ListProductsComponent),
				new MenuItem('product-plan-settings', ProductPlanSettingsComponent),
				new MenuItem('edit-variant-lists', ListVariantListsComponent),
				new MenuItem('users', ListUserComponent),
				new MenuItem('pools', ListPoolComponent),
				new MenuItem('user-teams', ListTeamComponent),
				new MenuItem('taskflow-templates', ListTaskflowTemplateComponent),
				new MenuItem('queries', ListQueryComponent),
				new MenuItem('system-logs', ListLogComponent),
				new MenuItem('mail-templates', ListMailTemplatesComponent),
				new MenuItem('list-questionnaries', ListQuestionnairesComponent),
				new MenuItem('task-area-admin', ListTaskAreasComponent),
				new MenuItem('accounting-plan-setups', ListAccountingPlanSetupsComponent),
				new MenuItem('list-reasons', ListReasonsComponent),
				new MenuItem('dashboards', ListDashboardComponent),
				new MenuItem('clean-up-domains', CleanUpDomainsComponent)
			]),
			new SubMenu('sales', [
				new MenuItem('approve-sales', ListUnapprovedSalesComponent),
				new MenuItem('list-orders-with-warnings', ListOrdersWithWarningsComponent),
				new MenuItem('company-search', TestSearchCompanyComponent),
				new MenuItem('open-calls', OpenCallsComponent),
				new MenuItem('my-orders', MyOrdersComponent),
				new MenuItem('leads', LeadsListComponent),
				new MenuItem('search-product-plans', SearchProductPlansComponent)
			]),
			new SubMenu('finance', [
				new MenuItem('invoice-control-list', InvoiceControlListComponent),
				new MenuItem('search-ledger-posts', SearchLedgerPostsComponent),
				new MenuItem('search-objections', InvoiceObjectionSearchComponent),
				new MenuItem('search-overdue-invoices', InvoiceOverdueSearchComponent),
				new MenuItem('settlements-list', SettlementsListComponent),
				new MenuItem('edit-price-adjustments', ListPriceAdjustmentTablesComponent),
				new MenuItem('overridden-due-dates', OverriddenDueDatesComponent)
			]),
			new SubMenu('customer-advice', [
				new MenuItem('unassigned-qualification-calls', UnassignedQualificationCallsComponent),
				new MenuItem('order-onboarding-list', OrderOnboardingListComponent)
			]),
			new SubMenu('production', [
				new MenuItem('order-pipeline', OrderPipelineComponent)
			]));

		if (environment.production == 'false') {
			this.menuTemplate.subMenus.push(new SubMenu('Debug', [
				new MenuItem('debug-inputs', DebugInputFieldsComponent),
				new MenuItem('debug-layout', DebugPanelLayoutComponent),
				new MenuItem('debug-context-menu', DebugContextMenuComponent),
				new MenuItem('debug-list-view', DebugListViewComponent),
				new MenuItem('debug-order', DebugOrderComponent),
				new MenuItem('sync-data', SyncDataComponent)
			]));
		}

		this.setAvailableMenuItems();

		this.navigation.onNavigation().subscribe((type) => {
			this.activeSubMenu = undefined;
			this.activeMenuItem = undefined;

			for (let subMenu of this.menu.subMenus) {
				for (let menuItem of subMenu.menuItems) {
					if (menuItem.component === type) {
						this.activeSubMenu = subMenu;
						this.activeMenuItem = menuItem;
					}
				}
			}
		});
	}

	openUserSettings() {
		this.navigation.openPopup(UserSettingsComponent, 'navmenu.user-settings.title');
	}

	setQuickLinks() {

		this.quickLinks = [];
		this.quickLinks.push(new QuickLinkItem('clipboard-list', ListTaskActionsComponent));

		this.poolService.getCurrentUserPools().then(pools => {
			pools.forEach(pool => {
				this.quickLinks.push(new QuickLinkItem(pool.icon as IconType, ListTaskActionsComponent, pool.name, pool.id!));
			})
		})
	}

	private setAvailableMenuItems() {

		const menuItemComponents: Type<Object>[] = [];
		this.menuTemplate.subMenus.forEach((subMenu) => {
			subMenu.menuItems.forEach(menuItem => {
				menuItemComponents.push(menuItem.component);
			});
		});
		const allowedComponents = this.credentialService.filterAccessibleComponents(menuItemComponents);
		const menus: SubMenu[] = [];

		this.menuTemplate.subMenus.forEach(submenu => {
			let tempSubMenu = new SubMenu(submenu.label, []);
			submenu.menuItems.forEach(menuItem => {
				let hasAccess = allowedComponents.some(component => component == menuItem.component);
				if (hasAccess) tempSubMenu.menuItems.push(menuItem);
			});
			if (tempSubMenu.menuItems.length > 0) menus.push(tempSubMenu);
		});
		this.menu.subMenus = menus;
	}

	navigateQuickLink(link: QuickLinkItem) {
		this.navigation.refreshView(ListTaskActionsComponent).then(listTaskActionComponent => {
			listTaskActionComponent.poolId = link.poolId!;
		});
	}

	navigate(menuItem: MenuItem) {
		this.navigation.refreshView(menuItem.component).then((view) => { menuItem.afterNavigation && menuItem.afterNavigation(view) });
	}

	ngOnDestroy() {
		this.poolChangeSubscription.unsubscribe();
		this.credentialSubscription.unsubscribe();
	}
}

class QuickLinkItem {
	constructor(public readonly icon: IconType, public readonly component: Type<Object>, public readonly title?: string, public readonly poolId: string = '') { }
}
class MenuItem {
	constructor(public readonly label: string, public readonly component: Type<Object>, public readonly afterNavigation?: (view: any) => void) { }
}
class SubMenu {
	constructor(public readonly label: string, public readonly menuItems: MenuItem[]) { }
}
class Menu {
	subMenus: SubMenu[] = [];
}
